import { useMemo, type JSX } from 'react';
import type { RecordingSettings } from '../../../core/model';
import type { RecordingFormat } from '../../../output/api';
import { NumberField } from '../../components/NumberField';
import { useProject, useUiContext } from '../../context';
import { attempt } from '../../safe';

export function OutputTab(): JSX.Element {
  const { studio, ui } = useUiContext();
  const rec = useProject((p) => p.output.recording);
  const formats = useMemo<RecordingFormat[]>(() => {
    try {
      return studio.listRecordingFormats();
    } catch {
      return [];
    }
  }, [studio]);
  const set = (patch: Partial<RecordingSettings>): void => void attempt(studio, () => studio.commands.setRecordingSettings(patch));
  const known = rec.mimeType === 'auto' || formats.some((f) => f.mimeType === rec.mimeType);
  const opfs = typeof navigator !== 'undefined' && typeof navigator.storage?.getDirectory === 'function';

  return (
    <div>
      <div className="ui-section-title">Recording</div>
      <div className="ui-field">
        <label htmlFor="settings-rec-format">Format</label>
        <select id="settings-rec-format" className="ui-select" value={rec.mimeType} onChange={(e) => set({ mimeType: e.target.value })}>
          <option value="auto">Automatic (best supported)</option>
          {!known ? <option value={rec.mimeType}>{rec.mimeType} (not supported in this browser)</option> : null}
          {formats.map((format) => (
            <option key={format.mimeType} value={format.mimeType}>
              {format.label} (.{format.fileExtension})
            </option>
          ))}
        </select>
        {formats.length === 0 ? <span className="ui-field-help ui-warn-text">This browser reports no supported recording formats.</span> : null}
        {!known ? <span className="ui-field-help ui-warn-text">The saved format is not available here; recording will fail until another is chosen.</span> : null}
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Video bitrate</span>
        <NumberField label="Recording video bitrate" value={rec.videoKbps} min={100} max={100000} step={500} unit="kb/s" precision={0} onChange={(videoKbps) => set({ videoKbps })} />
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Audio bitrate</span>
        <NumberField label="Recording audio bitrate" value={rec.audioKbps} min={16} max={510} step={8} unit="kb/s" precision={0} onChange={(audioKbps) => set({ audioKbps })} />
      </div>
      <div className="ui-section-title">Where recordings are written</div>
      <div role="radiogroup" aria-label="Recording storage" className="ui-col" style={{ gap: 4 }}>
        <label className="ui-check">
          <input type="radio" name="rec-sink" checked={rec.sink === 'opfs'} onChange={() => set({ sink: 'opfs' })} />
          Browser storage — written as it records; suited to long sessions
        </label>
        <label className="ui-check">
          <input type="radio" name="rec-sink" checked={rec.sink === 'memory'} onChange={() => set({ sink: 'memory' })} />
          Memory — kept in RAM until downloaded; lost when the tab closes
        </label>
      </div>
      {rec.sink === 'opfs' && !opfs ? <p className="ui-warn-text">This browser has no origin-private file system; choose Memory.</p> : null}
      <p className="ui-dim">Finished recordings stay in this browser until downloaded or deleted.</p>
      <button type="button" className="ui-btn" onClick={() => ui.getState().openDialog({ type: 'recordings' })}>
        Show recordings…
      </button>
    </div>
  );
}
